import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Rating,
  TextField,
  Button,
  Stack,
  Divider,
  Grid,
  MenuItem,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import api from "../api/axios"; // optional, for later submit

const QUESTIONS = [
  {
    id: "quality",
    label: "Quality of Service",
  },
  {
    id: "timeliness",
    label: "Timeliness of Delivery",
  },
  {
    id: "communication",
    label: "Communication & Coordination",
  },
  {
    id: "professionalism",
    label: "Professionalism of Team",
  },
  {
    id: "overall",
    label: "Overall Experience",
  },
];

export default function CustomerFeedback() {
  const [form, setForm] = useState({
    department: "",
    pocName: "",
    pocWhatsapp: "",
    ratings: {},
    remark: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);

  const fetchDepartments = async () => {
    const { data: response } = await api.get("/departments/list");
    setDepartments(response.data);
  };

  useEffect(() => {
    try {
      // Preload departments from API
      fetchDepartments();
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleRatingChange = (id, value) => {
    setForm((prev) => ({
      ...prev,
      ratings: {
        ...prev.ratings,
        [id]: value,
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (!form.department || !form.pocName || !form.pocWhatsapp) {
        alert("Please fill in all required fields.");
        return;
      }

      const response = await api.post("/feedback/save", form);
      if (response.data.success) {
        alert("Thank you for your valuable feedback!");
        setForm({
          department: "",
          pocName: "",
          pocWhatsapp: "",
          ratings: {},
          remark: "",
        });
      } else {
        alert("Unable to submit feedback. Please try again.");
        return;
      }
    } catch (err) {
      console.error(err);
      alert("Unable to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: 600,
          p: 4,
          borderRadius: 3,

          /* Glass / Dark Theme */
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={1} textAlign="center">
          Customer Feedback
        </Typography>

        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}
        >
          Please rate your experience with us
        </Typography>

        {/* Lead Department */}
        <Grid container direction="column" spacing={3} sx={{ mb: 2 }}>
          <Grid>
            <TextField
              select
              label="Project Department"
              name="department"
              value={form.department}
              onChange={handleChange}
              fullWidth
              required
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.dept_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* POC Name */}
          <Grid>
            <TextField
              label="Customer Name"
              name="pocName"
              value={form.pocName}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          {/* POC WhatsApp Number */}
          <Grid>
            <TextField
              label="WhatsApp Number"
              name="pocWhatsapp"
              value={form.pocWhatsapp}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", mb: 2 }} />

        <Stack spacing={3}>
          {QUESTIONS.map((q) => (
            <Box key={q.id}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                mb={1}
                sx={{ color: "primary.light" }}
              >
                {q.label}
              </Typography>

              <Rating
                value={form.ratings[q.id] || 0}
                onChange={(_, value) => handleRatingChange(q.id, value)}
                size="large"
                icon={<StarIcon fontSize="inherit" />}
                emptyIcon={
                  <StarIcon fontSize="inherit" sx={{ opacity: 0.3 }} />
                }
              />
            </Box>
          ))}

          <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

          {/* REMARK */}
          <TextField
            label="Additional Remarks (Optional)"
            multiline
            minRows={3}
            name="remark"
            value={form.remark}
            onChange={handleChange}
            fullWidth
          />

          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              height: 52,
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            {submitting ? "Submitting..." : "Submit Feedback"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
